/**
 * Type definitions for video templates
 */

// Template IDs
export type TemplateId =
  | 'MinimalistReveal'
  | 'UrgencyFlashSale'
  | 'SocialProofShowcase'
  | 'FeatureDemoFlow';

/**
 * Visual effects configuration
 * These can be toggled by users in ApprovalModalV2
 * and suggested by LLM based on product/brand context
 */
export interface VisualEffectsConfig {
  /** Funkelnde Partikel um Produkt/Elemente */
  sparkles?: boolean;
  /** Schwebende geometrische Formen im Hintergrund */
  floatingShapes?: boolean;
  /** Bokeh Lichteffekte (weiche Lichtpunkte) */
  bokeh?: boolean;
  /** Ambiente Beleuchtung von oben */
  ambientGlow?: boolean;
  /** Shimmer/Glanz-Effekt über Preis */
  priceShimmer?: boolean;
  /** Pulsierender Glow-Rand */
  pulsingGlow?: boolean;
}

/**
 * Default effect configuration - subtle premium look
 * Note: floatingShapes and bokeh are off by default to reduce visual overload
 * and respect users who may be sensitive to excessive motion/effects.
 */
export const DEFAULT_VISUAL_EFFECTS: VisualEffectsConfig = {
  sparkles: true,
  floatingShapes: false,  // Off by default - can be enabled by user
  bokeh: false,           // Off by default - can be enabled by user
  ambientGlow: true,
  priceShimmer: true,
  pulsingGlow: false,
};

/**
 * LLM style suggestions for colors
 * The LLM can recommend colors based on product/brand
 */
/** Background type options */
export type BackgroundType = 'none' | 'aurora' | 'animated_gradient' | 'retro_grid' | 'ripple';

/** Available gradient palettes for animated_gradient background */
export type GradientPalette =
  | 'sunset' | 'aqua' | 'aurora' | 'candy' | 'forest' | 'ocean'
  | 'peach' | 'lemon' | 'rose' | 'sky' | 'lava' | 'mint'
  | 'grape' | 'sand' | 'luxury' | 'midnight' | 'neon';

/** Available presets for retro_grid background */
export type RetroGridPreset =
  | 'synthwave' | 'cyberpunk' | 'neon' | 'vaporwave'
  | 'midnight' | 'sunset' | 'matrix' | 'tron';

/** Text animation type options */
export type TextAnimationType =
  | 'typewriter'    // Character by character typing
  | 'word_reveal'   // Word by word with spring physics + slide up
  | 'blur_fade'     // Word by word blur to sharp fade
  | 'slide_up'      // Words slide up from below
  | 'fade_scale'    // Words fade in with subtle scale
  | 'bounce'        // Words bounce in with spring physics
  | 'wave'          // Wave-like entrance
  | 'flip';         // Words flip in from top (3D rotate)

/**
 * Font IDs for caption text
 * Core fonts (all plans): Classic, Modern, Strong, Typewriter, Neon, Literature, Directional
 * Pro fonts (pro_fonts feature): Signature, Editor, Bubble, Squeeze, Poster, Deco, Elegant, Meme
 */
export type CaptionFontId =
  // Core fonts (free)
  | 'Classic'       // Playfair Display - Elegant serif
  | 'Modern'        // Inter - Clean sans-serif
  | 'Strong'        // Bebas Neue - Bold display
  | 'Typewriter'    // Courier Prime - Monospace vintage
  | 'Neon'          // Pacifico - Playful script
  | 'Literature'    // EB Garamond - Formal serif
  | 'Directional'   // Teko - Angular call-to-action
  // Pro fonts (premium)
  | 'Signature'     // Dancing Script - Handwritten
  | 'Editor'        // JetBrains Mono - Tech/coding
  | 'Bubble'        // Fredoka One - Rounded playful
  | 'Squeeze'       // Antonio - Condensed
  | 'Poster'        // Libre Baskerville - Classic poster
  | 'Deco'          // Righteous - Art deco retro
  | 'Elegant'       // Cormorant Garamond - Luxury serif
  | 'Meme';         // Comic Neue - Comic style

/** Map font IDs to CSS font families */
export const FONT_ID_TO_FAMILY: Record<CaptionFontId, string> = {
  // Core fonts
  Classic: 'Playfair Display, serif',
  Modern: 'Inter, sans-serif',
  Strong: 'Bebas Neue, sans-serif',
  Typewriter: 'Courier Prime, monospace',
  Neon: 'Pacifico, cursive',
  Literature: 'EB Garamond, serif',
  Directional: 'Teko, sans-serif',
  // Pro fonts
  Signature: 'Dancing Script, cursive',
  Editor: 'JetBrains Mono, monospace',
  Bubble: 'Fredoka One, sans-serif',
  Squeeze: 'Antonio, sans-serif',
  Poster: 'Libre Baskerville, serif',
  Deco: 'Righteous, sans-serif',
  Elegant: 'Cormorant Garamond, serif',
  Meme: 'Comic Neue, cursive',
};

export interface LLMStyleSuggestion {
  /** Background type: aurora (subtle) or animated_gradient (dynamic) */
  backgroundType?: BackgroundType;
  /** Color palette for animated_gradient background */
  backgroundPalette?: GradientPalette;
  /** Background/gradient primary color */
  backgroundColor?: string;
  /** Caption text color */
  captionColor?: string;
  /** Brand accent color (for highlights, CTAs) */
  accentColor?: string;
  /** Headline text for top of video (e.g., "Our Featured Product") */
  headlineText?: string;
}

/**
 * LLM suggestion schema for effects and styles
 * The LLM can recommend which effects and colors suit the product/brand
 */
export interface LLMSuggestion {
  /** Visual effects configuration */
  effects?: VisualEffectsConfig;
  /** Color/style suggestions */
  style?: LLMStyleSuggestion;
  /** Why these choices were made */
  reasoning?: string;
}

// Base props shared by all templates
// Index signature required for Remotion compatibility
export interface BaseTemplateProps {
  [key: string]: unknown;
  productTitle: string;
  productImage: string;
  salePrice: string;
  // Phase 2 caption (LLM-generated, displayed as animated overlay)
  captionText?: string;
  captionColor?: string;
  // Caption font (user-selectable, subscription-based availability)
  captionFont?: CaptionFontId;
  // Visual effects configuration (user-toggleable, LLM-suggested)
  effects?: VisualEffectsConfig;
  // Background configuration (LLM-suggested, user-switchable)
  backgroundType?: BackgroundType;
  backgroundPalette?: GradientPalette;
  // Text animation style (LLM-suggested, user-switchable)
  textAnimation?: TextAnimationType;
  // Language code for CTA texts (from brand settings)
  language?: string;
  /** Show price disclaimer (PAngV) - "inkl. MwSt., zzgl. Versand" */
  showPriceDisclaimer?: boolean;
  /** Optional discount code (e.g., "SAVE20") - shown via DiscountBadge */
  discountCode?: string;
  /** Optional discount label (max 40 chars, e.g., "Exklusiv für Follower") */
  discountLabel?: string;
}

// Minimalist Reveal Template
export interface MinimalistRevealProps extends BaseTemplateProps {
  originalPrice: string;
  brandColor: string;
  brandLogo?: string;
  rating?: number;
  reviewCount?: number;
  /** Headline text shown at top (e.g., "Featured Product", "Check this out") */
  headlineText?: string;
}

// Urgency Flash Sale Template
export interface UrgencyFlashSaleProps extends BaseTemplateProps {
  originalPrice: string;
  discountPercent: number;
  /** Optional - only show countdown if provided (prevents fake urgency) */
  countdownSeconds?: number;
  /** Optional - only show stock warning if provided (prevents fake scarcity) */
  stockCount?: number;
}

// Social Proof Showcase Template
export interface SocialProofShowcaseProps extends BaseTemplateProps {
  /** Optional - only show testimonial if provided (prevents fabricated quotes) */
  testimonialQuote?: string;
  /** Optional - only show customer name if testimonial provided */
  customerName?: string;
  /** Optional - only shown if rating >= 4.0 AND reviewCount >= 5 (quality threshold) */
  rating?: number;
  /** Optional - requires minimum 5 reviews to display (prevents inflated stats) */
  reviewCount?: number;
  /** Optional - only show if provided (prevents fabricated numbers) */
  customerCount?: number;
}

// Feature Demo Flow Template
export interface FeatureDemoFlowProps extends BaseTemplateProps {
  problemDescription: string;
  problemImage?: string;
  feature1: string;
  feature2?: string;
  feature3?: string;
  benefitDescription: string;
  resultImage?: string;
}

// Union type for all template props
export type TemplateProps =
  | MinimalistRevealProps
  | UrgencyFlashSaleProps
  | SocialProofShowcaseProps
  | FeatureDemoFlowProps;

// Video configuration
export interface VideoConfig {
  width: number;
  height: number;
  fps: number;
  durationInFrames: number;
}

export const DEFAULT_VIDEO_CONFIG: VideoConfig = {
  width: 1080,
  height: 1920,
  fps: 60,
  durationInFrames: 900, // 15 seconds @ 60fps
};
